import { ShieldCheck, FileCheck, HeadphonesIcon, Award } from "lucide-react";

const LOGO_URL =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_efficiency-certified/artifacts/qad9oyjx_661_-_maprimerenov-anah.jpg";

/**
 * AccompagnementBadge — Section "Partenaires officiels" à insérer sur les pages
 * `/particuliers`, `/simulateur-pac`, Home. Variant `compact` ou `full`.
 */
export default function AccompagnementBadge({ variant = "full", theme = "dark" }) {
  const isCompact = variant === "compact";
  const isDark = theme === "dark";

  const surface = isDark
    ? "bg-white/[0.03] border-white/10"
    : "bg-white border-slate-200 shadow-sm";
  const heading = isDark ? "text-white" : "text-slate-900";
  const body = isDark ? "text-white/70" : "text-slate-600";
  const badge = isDark
    ? "border-[#10B981]/25 bg-[#10B981]/[0.06] text-[#10B981]"
    : "border-emerald-200 bg-emerald-50 text-emerald-700";
  const chipBg = isDark ? "bg-white/[0.04] border-white/10 text-white/80" : "bg-slate-50 border-slate-200 text-slate-700";

  return (
    <section
      data-testid="accompagnement-badge"
      className={`rounded-3xl border ${surface} p-6 sm:p-8 lg:p-10`}
    >
      <div className={`grid ${isCompact ? "grid-cols-1 md:grid-cols-[auto_1fr]" : "grid-cols-1 lg:grid-cols-[minmax(0,320px)_1fr]"} gap-6 sm:gap-8 items-center`}>
        {/* Logo officiel */}
        <div className="flex items-center justify-center bg-white rounded-2xl p-5 sm:p-6 border border-slate-200/60">
          <img
            src={LOGO_URL}
            alt="MaPrimeRénov' — Agence Nationale de l'Habitat (Anah)"
            className="w-full max-w-[280px] h-auto object-contain"
            loading="lazy"
            data-testid="accompagnement-logos"
          />
        </div>

        {/* Texte */}
        <div>
          <div className={`inline-flex items-center gap-2 rounded-full border ${badge} px-3 py-1.5 mb-4`}>
            <Award className="h-3.5 w-3.5" />
            <span className="text-[10px] uppercase tracking-[0.28em] font-bold">
              Accompagnement officiel
            </span>
          </div>
          <h3 className={`font-display text-2xl sm:text-3xl font-bold ${heading} tracking-tight leading-[1.15] mb-3`}>
            {isCompact
              ? "Mandataire officiel MPR · Anah · CEE."
              : "ECONATION, mandataire officiel MPR, Anah & CEE."}
          </h3>
          <p className={`${body} text-sm sm:text-base leading-relaxed mb-5 max-w-2xl`}>
            Nous constituons et déposons vos dossiers <strong className={heading}>MaPrimeRénov&apos; (MPR)</strong> auprès
            de l&apos;<strong className={heading}>Agence Nationale de l&apos;Habitat (Anah)</strong> ainsi que vos
            <strong className={heading}> Certificats d&apos;Économies d&apos;Énergie (CEE)</strong>, et assurons
            le suivi jusqu&apos;au versement des aides. Vous n&apos;avez rien à faire — nous nous occupons de tout.
          </p>

          {!isCompact && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {[
                { icon: FileCheck, label: "Constitution du dossier" },
                { icon: ShieldCheck, label: "Dépôt auprès de l'Anah" },
                { icon: HeadphonesIcon, label: "Suivi jusqu'au versement" },
              ].map((item) => (
                <div
                  key={item.label}
                  className={`flex items-center gap-2.5 rounded-xl border ${chipBg} px-3.5 py-2.5`}
                >
                  <item.icon className="h-4 w-4 flex-shrink-0 text-[#10B981]" />
                  <span className="text-xs sm:text-sm font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          )}

          <div className={`mt-5 flex items-start gap-2 text-xs ${isDark ? "text-white/45" : "text-slate-500"} leading-relaxed`}>
            <ShieldCheck className="h-3.5 w-3.5 text-[#10B981] mt-0.5 flex-shrink-0" />
            <span>
              Les logos MaPrimeRénov&apos; et Anah sont la propriété de l&apos;État français —
              ECONATION est un mandataire indépendant qui vous accompagne dans vos démarches administratives.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
